import {
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useUsersMe } from "@/entities/users/hooks";
import {
  ProposalRefusalResponse,
  ProposalSigningResponse,
  ProposalSummaryResponse,
  ProposalVersionHistoryResponse,
  ReviseProposalRequest,
  SavedProposalResponse,
  SaveRecipientRequest,
  useActiveProposalTemplate,
  useProposalsApi
} from "./api";
import {
  composeValues,
  releaseDocumentUrlsWithCache,
  toProposalSlot
} from "./generatedDocument";
import { includedOf } from "./document/withValues";
import type { ResolvedDocument } from "./document/types";
import { NO_RECIPIENTS } from "./recipients/recipients";
import { SendStillRunningError } from "./sendErrorMessage";
import { ReviseStillRunningError } from "./reviseErrorMessage";
import { isTracked, TRACKING_POLL_MS } from "./tracking";
import { earlierSentVersions } from "./versionAsProposal";
import {
  Proposal,
  ProposalAuthor,
  ProposalDocument,
  ProposalRecipientRole,
  ProposalRecipients,
  ProposalHistoryVersion,
  ProposalStatus,
  ProposalTemplate,
  ProposalTracking,
  ProposalVersion,
  SlotValues,
  StoredDraft
} from "./types";

const isNotFound = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 404;

const toSigning = (
  signing: ProposalSigningResponse | null | undefined
): Pick<Proposal, "signedCount" | "signerCount" | "recipientStates"> =>
  signing
    ? {
        signedCount: signing.signed,
        signerCount: signing.total,
        recipientStates: signing.recipients.map((state) => ({
          email: state.email,
          signed: state.signed,
          viewedAt: state.viewedAt ?? undefined,
          declinedAt: state.declinedAt ?? undefined,
          declineReason: state.declineReason ?? undefined
        }))
      }
    : {};

const toProposal = (summary: ProposalSummaryResponse): Proposal => ({
  id: summary.id,
  name: summary.name,
  status: summary.status as ProposalStatus,
  createdAt: summary.createdAt,
  updatedAt: summary.updatedAt,
  createdBy: summary.createdBy,
  outOfDate: summary.outOfDate,
  ...toSigning(summary.signing),
  actions: summary.actions.map((action) => action.name)
});

const toStoredDraft = (saved: SavedProposalResponse): StoredDraft => ({
  proposal: toProposal(saved.proposal),
  version: {
    id: saved.version.id,
    lastModifiedAt: saved.version.lastModifiedAt
  }
});

const toRecipients = (
  recipients: SavedProposalResponse["recipients"]
): ProposalRecipients => ({
  ordered: recipients.ordered,
  list: recipients.list.map((recipient) => ({
    key: recipient.key,
    personId: recipient.personId ?? undefined,
    name: recipient.name,
    email: recipient.email,
    company: recipient.company ?? undefined,
    jobTitle: recipient.jobTitle ?? undefined,
    role: recipient.role as ProposalRecipientRole
  }))
});

// A recipient's status is the vendor's to report, never the editor's to save.
const toSaveRecipients = (recipients: ProposalRecipients) => ({
  ordered: recipients.ordered,
  list: recipients.list.map(
    ({ key, personId, name, email, company, jobTitle, role }) =>
      ({
        key,
        personId,
        name,
        email,
        company,
        jobTitle,
        role
      }) satisfies SaveRecipientRequest
  )
});

const proposalsKey = (dealId: string | undefined) => [
  "contracts/proposals",
  dealId
];

// While a proposal is out, asks the vendor for its status when the page opens
// and on each poll, rather than waiting for the e-sign scan's next run, and
// re-reads the list once it has answered. The list keeps its own poll, so a
// slow or failed ask never holds it back.
const useSigningRefresh = (dealId: string | undefined, tracked: boolean) => {
  const api = useProposalsApi();
  const queryClient = useQueryClient();
  useQuery({
    queryKey: ["esign/requests/refresh", dealId],
    queryFn: async () => {
      try {
        await api.refreshSigning();
      } catch {
        // The list's own poll picks it up once the scheduled scan has run.
        return null;
      }
      await queryClient.invalidateQueries({ queryKey: proposalsKey(dealId) });
      return null;
    },
    enabled: tracked,
    // Each page that shows the deal's proposals asks again when it opens.
    staleTime: 0,
    refetchInterval: tracked ? TRACKING_POLL_MS : false,
    retry: false
  });
};

export const useProposals = (
  dealId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  const api = useProposalsApi();
  const enabled = options.enabled !== false && !!dealId;
  const result = useQuery({
    queryKey: proposalsKey(dealId),
    queryFn: async () => (await api.list(dealId ?? "")).map(toProposal),
    enabled,
    // Status comes in from the vendor on a schedule; re-read while any
    // proposal can still move, so the page follows it without a reload.
    refetchInterval: (query) =>
      query.state.data?.some(isTracked) ? TRACKING_POLL_MS : false
  });
  useSigningRefresh(dealId, enabled && !!result.data?.some(isTracked));
  return result;
};

const NO_AUTHOR: ProposalAuthor = { displayName: "", avatarUrl: "" };

// A version's last change: its last save, or a later status it moved to, such
// as being sent, viewed or voided, which leaves the version itself unwritten.
const lastChangedAt = (version: ProposalVersionHistoryResponse) => {
  const lastStatusAt = version.history?.at(-1)?.appliedAt;
  return lastStatusAt &&
    Date.parse(lastStatusAt) > Date.parse(version.lastModifiedAt)
    ? lastStatusAt
    : version.lastModifiedAt;
};

// Reads a contractsapi older than the recipients and author fields as having
// none, so the files the latest version went out with are still read.
const toHistoryVersion = (
  version: ProposalVersionHistoryResponse
): ProposalHistoryVersion => {
  const signing = toSigning(version.signing);
  return {
    id: version.id,
    status: version.status as ProposalStatus,
    createdAt: version.createdAt,
    updatedAt: lastChangedAt(version),
    createdBy: version.author ?? NO_AUTHOR,
    recipients: version.recipients
      ? toRecipients(version.recipients)
      : NO_RECIPIENTS,
    ...signing,
    recipientStates: signing.recipientStates ?? [],
    sentFileAttachmentId: version.sentFileAttachmentId ?? undefined,
    signedFileAttachmentId: version.signedFileAttachmentId ?? undefined
  };
};

// Every version of a proposal, newest first. A version that went out never
// changes, and any write to the proposal moves its updatedAt, so a read is
// kept until then.
const proposalVersionsQuery = (
  api: ReturnType<typeof useProposalsApi>,
  proposal: Pick<Proposal, "id" | "updatedAt"> | undefined
) =>
  queryOptions({
    queryKey: [
      "contracts/proposals/version-history",
      proposal?.id,
      proposal?.updatedAt
    ],
    queryFn: async () =>
      (await api.versions(proposal?.id ?? "")).versions.map(toHistoryVersion),
    enabled: !!proposal,
    staleTime: Infinity,
    // The last read stands in while the proposal's next one loads, so a status
    // change doesn't blank what's on screen; never another proposal's.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === proposal?.id ? previous : undefined
  });

export const useProposalVersions = (
  proposal: Pick<Proposal, "id" | "updatedAt"> | undefined
) => {
  const api = useProposalsApi();
  return useQuery(proposalVersionsQuery(api, proposal));
};

const toTracking = (versions: ProposalHistoryVersion[]): ProposalTracking => {
  const latest = versions[0];
  return {
    versionId: latest?.id,
    sentFileAttachmentId: latest?.sentFileAttachmentId,
    signedFileAttachmentId: latest?.signedFileAttachmentId
  };
};

// The latest version's stored files, re-read while the proposal can still
// move, since signing stores a new one. The same read as the version history.
export const useProposalTracking = (
  proposal:
    | Pick<Proposal, "id" | "status" | "actions" | "updatedAt">
    | undefined
) => {
  const api = useProposalsApi();
  const tracked = !!proposal && isTracked(proposal);
  return useQuery({
    ...proposalVersionsQuery(api, proposal),
    select: toTracking,
    enabled: !!proposal && proposal.status !== ProposalStatus.DRAFT,
    refetchInterval: tracked ? TRACKING_POLL_MS : false,
    // updatedAt changes on every write, including a Revise, so a proposal
    // sent again never shows the withdrawn version's files, even for a moment.
    placeholderData: undefined
  });
};

// The earlier versions of each proposal that went out, newest first, by
// proposal id.
export const useEarlierProposalVersions = (
  proposals: Pick<Proposal, "id" | "updatedAt">[]
): Record<string, ProposalHistoryVersion[]> => {
  const api = useProposalsApi();
  const results = useQueries({
    queries: proposals.map((proposal) => proposalVersionsQuery(api, proposal))
  });
  // A moved proposal's next read starts a new query, which useQueries gives no
  // placeholder; its last earlier versions stand in until that read lands.
  // Kept from the last committed render only, so an abandoned render never
  // stands in, and a proposal no longer listed is dropped.
  const lastRead = useRef<Record<string, ProposalHistoryVersion[]>>({});
  const earlier: Record<string, ProposalHistoryVersion[]> = Object.fromEntries(
    proposals.map((proposal, index) => {
      const versions = results[index]?.data;
      return [
        proposal.id,
        versions
          ? earlierSentVersions(versions)
          : (lastRead.current[proposal.id] ?? [])
      ];
    })
  );
  useEffect(() => {
    lastRead.current = earlier;
  });
  return earlier;
};

export const useHasActiveProposalTemplate = (
  options: { enabled?: boolean } = {}
) => {
  const result = useActiveProposalTemplate({
    enabled: options.enabled !== false
  });
  return {
    ...result,
    data: result.data ? !!result.data.template : undefined
  };
};

// Read from the template, not the generated document, so the editor can show
// the sections while a proposal is still generating, and the recipients step
// knows how many can sign before a proposal exists. Once one does, its own
// document says.
export const useProposalTemplate = (options: { enabled?: boolean } = {}) => {
  const result = useActiveProposalTemplate(options);
  const active = result.data?.template;
  const data = useMemo<ProposalTemplate | undefined>(
    () =>
      active
        ? {
            name: active.name,
            sections: active.sections,
            signaturePlaces: active.signaturePlaces
          }
        : undefined,
    [active]
  );
  return { ...result, data };
};

// The id a first save stores the proposal under, so a retried save finds the
// proposal it made: `prp-` and 16 hex characters, as contractsapi's own.
const newProposalId = () =>
  `prp-${Array.from(crypto.getRandomValues(new Uint8Array(8)), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("")}`;

// A proposal that was generated but never saved opens as a fresh draft rather
// than a dead page. It takes the template's name.
const draftProposal = (
  proposalId: string,
  name: string,
  createdBy: ProposalAuthor = NO_AUTHOR
): Proposal => {
  const now = new Date().toISOString();
  return {
    id: proposalId,
    name,
    status: ProposalStatus.DRAFT,
    createdAt: now,
    updatedAt: now,
    createdBy
  };
};

interface DocumentContext {
  // Absent for a proposal never saved, which opens as a fresh draft.
  proposal?: Proposal;
  proposalId: string;
  dealId: string;
  saved: boolean;
  createdBy?: ProposalAuthor;
}

// Generates from the deal's current terms, then composes the document. Nothing
// is stored: the rep's values start empty.
const buildDocument = async (
  api: ReturnType<typeof useProposalsApi>,
  context: DocumentContext
): Promise<ProposalDocument> => {
  const generated = await api.generate(context.dealId);
  const slots = generated.slots.map(toProposalSlot);
  const proposal =
    context.proposal ??
    draftProposal(
      context.proposalId,
      generated.proposalName,
      context.createdBy
    );
  const resolved = generated.document ?? undefined;
  const { url: documentUrl, ...composedFlags } = await api.compose({
    templateVersionId: generated.versionId,
    proposalName: proposal.name,
    values: composeValues(slots, {}),
    lines: generated.lines,
    ...(resolved ? { included: includedOf(resolved) } : {})
  });
  return {
    proposal,
    dealId: context.dealId,
    sections: generated.sections,
    slots,
    templateVersionId: generated.versionId,
    lines: generated.lines,
    signaturePlaces: generated.signaturePlaces,
    documentUrl,
    composedFlags,
    values: {},
    composedValues: {},
    recipients: NO_RECIPIENTS,
    saved: context.saved,
    resolved
  };
};

// A stored proposal, composed on its own template version with what the rep
// typed drawn in, so the document already holds them.
const savedDocument = async (
  api: ReturnType<typeof useProposalsApi>,
  saved: SavedProposalResponse,
  dealId: string
): Promise<ProposalDocument> => {
  const slots = saved.slots.map(toProposalSlot);
  const values: SlotValues = Object.fromEntries(
    saved.slots.flatMap((slot) =>
      slot.typedValue === null ? [] : [[slot.id, slot.typedValue]]
    )
  );
  const proposal = toProposal(saved.proposal);
  const resolved = saved.document ?? undefined;
  const { url: documentUrl, ...composedFlags } = await api.compose({
    templateVersionId: saved.version.templateVersionId,
    proposalName: proposal.name,
    values: composeValues(slots, values),
    lines: saved.lines,
    ...(resolved ? { included: includedOf(resolved) } : {})
  });
  return {
    proposal,
    dealId,
    sections: saved.sections,
    slots,
    templateVersionId: saved.version.templateVersionId,
    lines: saved.lines,
    signaturePlaces: saved.signaturePlaces,
    documentUrl,
    composedFlags,
    values,
    composedValues: values,
    recipients: toRecipients(saved.recipients),
    saved: true,
    version: {
      id: saved.version.id,
      lastModifiedAt: saved.version.lastModifiedAt
    },
    resolved
  };
};

const DOCUMENT_QUERY = "contracts/proposals/document";

const documentKey = (
  dealId: string | undefined,
  proposalId: string | undefined
) => [DOCUMENT_QUERY, dealId, proposalId];

const DELETED_QUERY = "contracts/proposals/deleted";

const deletedKey = (
  dealId: string | undefined,
  proposalId: string | undefined
) => [DELETED_QUERY, dealId, proposalId];

// Whether this session deleted the proposal; it is never opened again as a draft.
export const useIsProposalDeleted = (
  dealId: string | undefined,
  proposalId: string | undefined
) =>
  useQuery({
    queryKey: deletedKey(dealId, proposalId),
    queryFn: () => false,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity
  }).data === true;

const PREVIEW_QUERY = "contracts/proposals/preview";
// A superseded preview's PDF is released this long after its query was last
// in use. It can still be on screen while a newer compose runs, so this is
// well past the longest a compose can take before the gateway times it out.
const PREVIEW_GC_MS = 5 * 60 * 1000;

const STORED_FILE_QUERY = "contracts/proposals/stored-file";

const useReleaseCachedDocumentUrls = (queryKeyPrefix = DOCUMENT_QUERY) => {
  const queryClient = useQueryClient();
  useEffect(
    () =>
      releaseDocumentUrlsWithCache(queryClient.getQueryCache(), queryKeyPrefix),
    [queryClient, queryKeyPrefix]
  );
};

// A stored proposal opens as saved. One contractsapi doesn't hold was
// generated and never saved; it opens as a fresh draft. Generate seeds the
// document on the way in, so opening a new proposal doesn't generate it twice.
// Re-reads an open proposal, as when the version on screen is replaced elsewhere.
export const useRefreshProposalDocument = (
  dealId: string | undefined,
  proposalId: string | undefined
) => {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: documentKey(dealId, proposalId)
      }),
    [queryClient, dealId, proposalId]
  );
};

export const useProposalDocument = (
  dealId: string | undefined,
  proposalId: string | undefined
) => {
  const api = useProposalsApi();
  const deleted = useIsProposalDeleted(dealId, proposalId);
  useReleaseCachedDocumentUrls();
  return useQuery({
    queryKey: documentKey(dealId, proposalId),
    queryFn: async () => {
      const resolvedDealId = dealId ?? "";
      const resolvedProposalId = proposalId ?? "";
      let saved: SavedProposalResponse;
      try {
        saved = await api.open(resolvedProposalId);
      } catch (error) {
        if (!isNotFound(error)) throw error;
        return buildDocument(api, {
          proposalId: resolvedProposalId,
          dealId: resolvedDealId,
          saved: false
        });
      }
      return savedDocument(api, saved, resolvedDealId);
    },
    enabled: !!dealId && !!proposalId && !deleted,
    staleTime: Infinity,
    // Re-read a stored proposal on each open; use Generate's unsaved copy as seeded
    refetchOnMount: ({ state }) =>
      state.data?.saved === false ? false : "always"
  });
};

interface PreviewParams {
  api: ReturnType<typeof useProposalsApi>;
  document: ProposalDocument;
  values: SlotValues;
  // A flowing template's optional-block choices; the document's own when absent.
  included?: Record<string, boolean>;
}

const includedFor = ({ document, included }: PreviewParams) =>
  document.resolved ? (included ?? includedOf(document.resolved)) : undefined;

// Recomposes the document with the rep's unsaved values, on the template
// version and lines it was generated with. The same values always compose the
// same document, so a result never goes stale; a compose that a newer one
// supersedes is aborted. Keyed by the document's own PDF, so a Regenerate
// starts afresh.
export const proposalPreviewQuery = (params: PreviewParams) => {
  const { api, document, values } = params;
  const included = includedFor(params);
  return queryOptions({
    queryKey: [
      PREVIEW_QUERY,
      document.proposal.id,
      document.documentUrl,
      values,
      included
    ],
    queryFn: async ({ signal }) => {
      const { url, ...composedFlags } = await api.compose(
        {
          templateVersionId: document.templateVersionId,
          proposalName: document.proposal.name,
          values: composeValues(document.slots, values),
          lines: document.lines,
          ...(included ? { included } : {})
        },
        signal
      );
      return { documentUrl: url, composedFlags, composedValues: values };
    },
    staleTime: Infinity,
    gcTime: PREVIEW_GC_MS,
    // A failed compose is shown at once; the rep's next edit composes again.
    retry: false
  });
};

export const useProposalPreview = ({
  enabled,
  ...params
}: PreviewParams & { enabled: boolean }) => {
  useReleaseCachedDocumentUrls(PREVIEW_QUERY);
  return useQuery({
    ...proposalPreviewQuery(params),
    enabled,
    // The last preview stands in while the next composes, but only one of this
    // same document: after a Regenerate the old document's preview never shows
    // or downloads in its place.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === params.document.documentUrl
        ? previous
        : undefined
  });
};

const TEMPLATE_FILES_QUERY = "contracts/proposals/template-files";

// A flowing template version's background PDF and fonts, as local URLs. A
// version's files never change, so they are read once and kept.
export const useTemplateFiles = (resolved: ResolvedDocument | undefined) => {
  const api = useProposalsApi();
  const versionId = resolved?.templateVersionId;
  const fonts = resolved?.fonts;
  return useQuery({
    queryKey: [TEMPLATE_FILES_QUERY, versionId, fonts],
    queryFn: async () => {
      const id = versionId ?? "";
      const names = Object.keys(fonts ?? {});
      const [backgroundUrl, ...fontFiles] = await Promise.all([
        api.templateFile(id, "background.pdf"),
        ...names.map((name) => api.templateFile(id, (fonts ?? {})[name]))
      ]);
      return {
        backgroundUrl,
        fontUrls: Object.fromEntries(
          names.map((name, index) => [name, fontFiles[index]])
        )
      };
    },
    enabled: !!versionId,
    staleTime: Infinity,
    gcTime: Infinity
  });
};

// A stored file, what went out or the signed copy, shown from a local URL. A
// file never changes under its id, so it is read once per id.
export const useStoredProposalFile = (fileId: string | undefined) => {
  const api = useProposalsApi();
  useReleaseCachedDocumentUrls(STORED_FILE_QUERY);
  return useQuery({
    queryKey: [STORED_FILE_QUERY, fileId],
    queryFn: async () => ({
      documentUrl: await api.attachmentUrl(fileId ?? "")
    }),
    enabled: !!fileId,
    staleTime: Infinity,
    gcTime: PREVIEW_GC_MS
  });
};

// Generate and Regenerate are the same pure read: compute a document from
// current terms and return it, writing nothing. Only Save is a write, so no
// row appears in the Proposals table until a rep commits to one.
export const useGenerateProposal = () => {
  const { data: me } = useUsersMe();
  const api = useProposalsApi();
  const queryClient = useQueryClient();
  useReleaseCachedDocumentUrls();
  return useMutation({
    mutationFn: async (dealId: string) => {
      const document = await buildDocument(api, {
        proposalId: newProposalId(),
        dealId,
        saved: false,
        createdBy: {
          displayName: me?.displayName ?? "",
          avatarUrl: me?.avatarUrl ?? ""
        }
      });
      queryClient.setQueryData(
        documentKey(dealId, document.proposal.id),
        document
      );
      return document.proposal;
    }
  });
};

// Rebuilds the document from the latest deal data, replacing the rep's edits.
// Like Generate it writes nothing; the save that follows cuts the new version.
export const useRegenerateProposal = () => {
  const api = useProposalsApi();
  return useMutation({
    mutationFn: ({
      proposal,
      dealId,
      saved
    }: {
      proposal: Proposal;
      dealId: string;
      saved: boolean;
    }) =>
      buildDocument(api, { proposal, proposalId: proposal.id, dealId, saved })
  });
};

export const useRenameProposal = (dealId: string | undefined) => {
  const api = useProposalsApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ proposalId, name }: { proposalId: string; name: string }) =>
      api.rename(proposalId, name),
    onSuccess: (renamed) => {
      queryClient.invalidateQueries({ queryKey: proposalsKey(dealId) });
      queryClient.invalidateQueries({
        queryKey: documentKey(dealId, renamed.id)
      });
    }
  });
};

export interface SaveVariables {
  // The document on screen: its template version, and the version it opened at.
  document: ProposalDocument;
  // Carries the name, which before the first save lives only in the editor.
  proposal: Proposal;
  values: SlotValues;
  // A save that follows a Regenerate, which cuts a new version.
  regenerated: boolean;
  recipients: ProposalRecipients;
  // A flowing template's optional-block choices, by block id.
  included?: Record<string, boolean>;
}

// The first save creates the proposal under the id Generate gave it; later
// saves overwrite its draft, except one that follows a Regenerate, for which
// the back end cuts a new version.
export const useSaveProposal = (dealId: string | undefined) => {
  const api = useProposalsApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      document,
      proposal,
      values,
      regenerated,
      recipients,
      included
    }: SaveVariables) => {
      const body = {
        dealId: dealId ?? "",
        templateVersionId: document.templateVersionId,
        values,
        ...(included ? { included } : {}),
        recipients: toSaveRecipients(recipients)
      };
      const basedOn = document.version && {
        versionId: document.version.id,
        lastModifiedAt: document.version.lastModifiedAt
      };
      return (
        document.saved
          ? api.save({ ...body, basedOn, regenerated }, proposal.id)
          : api.save({ ...body, proposalId: proposal.id, name: proposal.name })
      ).then(toStoredDraft);
    },
    onSuccess: (stored) => {
      queryClient.invalidateQueries({ queryKey: proposalsKey(dealId) });
      queryClient.invalidateQueries({
        queryKey: documentKey(dealId, stored.proposal.id)
      });
    }
  });
};

const SEND_IN_PROGRESS = "SEND_IN_PROGRESS";
// Each attempt waits up to 25 s on the server, so this covers a Send's longest
// run with room to spare; a repeated Send carries on the one already started.
export const SEND_ATTEMPTS = 3;

const isStillSending = (
  reply: SavedProposalResponse | ProposalRefusalResponse
): reply is ProposalRefusalResponse =>
  "code" in reply && reply.code === SEND_IN_PROGRESS;

export interface SendVariables {
  proposalId: string;
  basedOn: ProposalVersion;
}

// Sends the draft for signature, asking again while contractsapi answers that
// the Send is still running. Whatever the outcome, the document is read again,
// so a Send that went out opens as sent and one that started stays locked.
export const useSendProposal = (dealId: string | undefined) => {
  const api = useProposalsApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ proposalId, basedOn }: SendVariables) => {
      const body = {
        dealId: dealId ?? "",
        basedOn: {
          versionId: basedOn.id,
          lastModifiedAt: basedOn.lastModifiedAt
        }
      };
      for (let attempt = 1; ; attempt += 1) {
        const reply = await api.send(proposalId, body);
        if (!isStillSending(reply)) return toStoredDraft(reply);
        if (attempt >= SEND_ATTEMPTS) throw new SendStillRunningError();
      }
    },
    // Flips the open editor to the sent view at once; the read below then
    // replaces the document with the one stored at the Send.
    onSuccess: (stored, { proposalId }) =>
      queryClient.setQueryData<ProposalDocument>(
        documentKey(dealId, proposalId),
        (current) =>
          current && {
            ...current,
            proposal: stored.proposal,
            version: stored.version,
            saved: true
          }
      ),
    onSettled: (_stored, _error, { proposalId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: proposalsKey(dealId) }),
        queryClient.invalidateQueries({
          queryKey: documentKey(dealId, proposalId)
        })
      ])
  });
};

const WITHDRAW_IN_PROGRESS = "WITHDRAW_IN_PROGRESS";
// Each answer can take contractsapi's whole wait (about 25 s), so asking
// three times covers well over a minute before the rep is told to check back.
export const REVISE_ATTEMPTS = 3;

const isStillWithdrawing = (
  reply: SavedProposalResponse | ProposalRefusalResponse
): reply is ProposalRefusalResponse =>
  "code" in reply && reply.code === WITHDRAW_IN_PROGRESS;

export interface ReviseVariables {
  proposalId: string;
  // The version the rep confirmed. A table row carries none, so it is read
  // when the Revise or Withdraw starts.
  basedOn?: ProposalVersion;
}

// Asks again while contractsapi answers that the withdraw is still running,
// for Revise and Withdraw alike.
const untilWithdrawn = async (
  ask: () => Promise<SavedProposalResponse | ProposalRefusalResponse>
) => {
  for (let attempt = 1; ; attempt += 1) {
    const reply = await ask();
    if (!isStillWithdrawing(reply)) return toStoredDraft(reply);
    if (attempt >= REVISE_ATTEMPTS) throw new ReviseStillRunningError();
  }
};

// The mutation Revise and Withdraw share; the proposal is read again either
// way, so the table and the open proposal show where it stands.
const useWithdrawingMutation = (
  dealId: string | undefined,
  ask: (
    proposalId: string,
    body: ReviseProposalRequest
  ) => Promise<SavedProposalResponse | ProposalRefusalResponse>
) => {
  const api = useProposalsApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ proposalId, basedOn }: ReviseVariables) => {
      const version =
        basedOn ?? toStoredDraft(await api.open(proposalId)).version;
      const body = {
        dealId: dealId ?? "",
        basedOn: {
          versionId: version.id,
          lastModifiedAt: version.lastModifiedAt
        }
      };
      return untilWithdrawn(() => ask(proposalId, body));
    },
    onSettled: (_stored, _error, { proposalId }) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: proposalsKey(dealId) }),
        queryClient.invalidateQueries({
          queryKey: documentKey(dealId, proposalId)
        })
      ])
  });
};

// Returns the proposal to Draft as a new version, withdrawing it first when
// it is still out for signature. A Revise that landed opens the new draft.
export const useReviseProposal = (dealId: string | undefined) => {
  const api = useProposalsApi();
  return useWithdrawingMutation(dealId, api.revise);
};

// Withdraws the signature request and leaves the proposal Voided, with no new
// draft; on a closed deal too, where Revise is refused.
export const useWithdrawProposal = (dealId: string | undefined) => {
  const api = useProposalsApi();
  return useWithdrawingMutation(dealId, api.withdraw);
};

// Who a proposal went to, for confirmations that name them. Read afresh for
// each confirmation, since a cached list can predate an edit and a Send.
export const useProposalRecipients = (proposalId: string | undefined) => {
  const api = useProposalsApi();
  return useQuery({
    queryKey: ["contracts/proposals/recipients", proposalId],
    queryFn: async () =>
      toRecipients((await api.open(proposalId ?? "")).recipients),
    enabled: !!proposalId,
    gcTime: 0
  });
};

// A proposal already gone, deleted from another tab, counts as deleted.
export const useDeleteProposal = (dealId: string | undefined) => {
  const api = useProposalsApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (proposalId: string) => {
      try {
        await api.remove(proposalId);
      } catch (error) {
        if (!isNotFound(error)) throw error;
      }
    },
    onSuccess: (_result, proposalId) => {
      // Kept for the session even when no page is watching it, as after a table delete
      queryClient.setQueryDefaults([DELETED_QUERY], { gcTime: Infinity });
      queryClient.setQueryData(deletedKey(dealId, proposalId), true);
      // Frees the deleted copy; an editor still open on it keeps its own until it leaves
      queryClient.removeQueries({
        queryKey: documentKey(dealId, proposalId),
        type: "inactive"
      });
      return queryClient.invalidateQueries({ queryKey: proposalsKey(dealId) });
    }
  });
};
